import React, { useRef, useState, useEffect, useCallback, Fragment } from 'react';
import {
  Stage, Layer, Line, Rect, Circle, RegularPolygon, Star, Arrow, Text, Transformer
} from 'react-konva';
import {
  FaPen, FaArrowRight, FaMinus, FaShapes, FaEraser, FaUndo, FaRedo, FaTrash, FaSave,
  FaRegCircle, FaSquareFull, FaStar, FaStop, FaMousePointer,
  FaCompress,
  FaExpand,
  FaTimes,
  FaPlay,
  FaPause
} from 'react-icons/fa';
import { BsTriangle } from 'react-icons/bs';
import { MdUpload } from "react-icons/md";
import { TbLineDashed, TbLineDotted } from 'react-icons/tb';
import { BiText } from "react-icons/bi";
import { MdFiberManualRecord, MdLabel } from 'react-icons/md';
import { TbTooltip, TbLabelFilled } from "react-icons/tb";
import { LuRectangleHorizontal } from "react-icons/lu";
import DraggableComponent from './DraggableComponent';
import DraggableLabel from './DraggableLabel';
import ErrorBoundary from './ErrorBoundary';
import { useDispatch } from 'react-redux';
import { fullScreenOff, fullScreenOn, getSubjectWhiteboard, uploadRecording } from '../../Redux/ActionCreators/whiteboardAction';
import { getChapters } from '../../Redux/ActionCreators/manageContentAction';
import { useSelector } from 'react-redux';
import Loader from '../../utils/backdrop';
import "./whiteboard.scss"

const SHAPES = [
  { key: 'arrow', label: 'Arrow', icon: <FaArrowRight /> },
  { key: 'line', label: 'Line', icon: <FaMinus /> },
  { key: 'rectangle', label: 'Rectangle', icon: <FaSquareFull /> },
  { key: 'square', label: 'Square', icon: <FaSquareFull /> },
  { key: 'circle', label: 'Circle', icon: <FaRegCircle /> },
  { key: 'triangle', label: 'Triangle', icon: <BsTriangle /> },
  { key: 'hexagon', label: 'Hexagon', icon: '⬡' },
  { key: 'star', label: 'Star', icon: <FaStar /> }
];

const LINE_TYPES = [
  { key: 'solid', label: 'Solid', icon: <FaMinus />, dash: [] },
  { key: 'dashed', label: 'Dashed', icon: <TbLineDashed />, dash: [20, 10] },
  { key: 'dotted', label: 'Dotted', icon: <TbLineDotted />, dash: [2, 10] }
];

const LABEL_PICKER = [
  { key: 'TOOLTIP_DOWN', label: 'Tooltip Down', icon: <TbTooltip /> },
  { key: 'LABEL_LEFT', label: 'Label Left', icon: <TbLabelFilled /> },
  { key: 'SIMPLE', label: 'Simple', icon: <LuRectangleHorizontal /> }
];

const TOOL_OPTIONS = [
  { key: 'select', label: 'Select', icon: <FaMousePointer /> },
  { key: 'pen', label: 'Pen', icon: <FaPen /> },
  { key: 'shape', label: 'Shapes', icon: <FaShapes /> },
  { key: 'line', label: 'Line', icon: <FaMinus /> },
  { key: 'text', label: 'Text', icon: <BiText /> },
  { key: 'label', label: 'Label', icon: <MdLabel /> },
  { key: 'eraser', label: 'Eraser', icon: <FaEraser /> },
  { key: 'image', label: 'Upload Image', icon: <MdUpload /> }
];

const INITIAL_COLOR = '#222222';
const INITIAL_WIDTH = 3;
const DEFAULT_HEIGHT = 700;

function uniqueId() {
  return 'id-' + Date.now() + '-' + Math.random().toString(36).substr(2, 5);
}

function Whiteboard() {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const dispatch = useDispatch();

  // Toggle fullscreen (just side nav)
  
  const [tool, setTool] = useState('pen');
  const [shapeType, setShapeType] = useState('rectangle');
  const [lineStyle, setLineStyle] = useState('solid');
  const [strokeColor, setStrokeColor] = useState(INITIAL_COLOR);
  const [strokeWidth, setStrokeWidth] = useState(INITIAL_WIDTH);

  const [shapes, setShapes] = useState([]);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);

  const [drawing, setDrawing] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [selectedLabelType, setSelectedLabelType] = useState('TOOLTIP_DOWN');
  const [showLabelBox, setShowLabelBox] = useState(false);
  const [textPos, setTextPos] = useState(null);

  const [showShapeBox, setShowShapeBox] = useState(false);
  const [showLineBox, setShowLineBox] = useState(false);

  // Responsive width/height
  const containerRef = useRef();
  const scrollDivRef = useRef();
  const [stageSize, setStageSize] = useState({ width: 0, height: DEFAULT_HEIGHT });

  // State for recording and exporting
  const [recording, setRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingInterval, setRecordingInterval] = useState(null);
  const recordingBlobRef = useRef(null);
  const exportImageRef = useRef(null);
  const isFormSubmittedRef = useRef(false);
  
  // Form state (shared between recording and export)
  const [showRecordingForm, setShowRecordingForm] = useState(false);
  const [formMode, setFormMode] = useState('recording'); // 'recording' or 'export'
  const [recordingTitle, setRecordingTitle] = useState('');
  const [recordingDescription, setRecordingDescription] = useState('');
  const [recordingSubjectId, setRecordingSubjectId] = useState('');
  const [recordingChapterId, setRecordingChapterId] = useState('');
  const [titleError, setTitleError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [chapterError, setChapterError] = useState('');
  // Selection/transform/edit states
  const [selectedIds, setSelectedIds] = useState([]);
  const transformerRef = useRef();
  const [editingTextId, setEditingTextId] = useState(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const [initialTextState, setInitialTextState] = useState(null);

  // For selection rectangle
  const [selection, setSelection] = useState({ visible: false, x: 0, y: 0, w: 0, h: 0 });
  const selectionRectRef = useRef();

  const stageRef = useRef();
  const inputRef = useRef(null);
  const { chapterList } = useSelector(state => state.manageContent);
  const { subjectWhiteboard, isUploading } = useSelector(state => state.whiteboard);
  const timerRef = useRef(null);
  const recordingTimeRef = useRef(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const activePointers = useRef(new Set());

  const startTimer = useCallback(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Initialize time reference
    recordingTimeRef.current = recordingTime;
    
    // Start a new timer
    timerRef.current = setInterval(() => {
      recordingTimeRef.current += 1;
      setRecordingTime(recordingTimeRef.current);
    }, 1000);
  }, [recordingTime]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // const [restrictedMode, setRestrictedMode] = useState(false);

  // // Check for restricted environment on component mount
  // useEffect(() => {
  //   if (isRestrictedEnvironment()) {
  //   const userAgent = navigator.userAgent.toLowerCase();
  //   const isSchoolProxy = userAgent.includes('websense') || 
  //                        userAgent.includes('smoothwall') ||
  //                        userAgent.includes('lightspeed');
  //   const isWebRTCBlocked = !navigator.mediaDevices || 
  //                          !navigator.mediaDevices.getUserMedia;
  //   const isHTTPS = window.location.protocol === 'https:';
  //     console.warn(`Running in restricted environment - some features may be limited: school proxy: ${userAgent}, WebRTC blocked: ${navigator.mediaDevices.getUserMedia}, HTTPS: ${isHTTPS}`);

  //     setRestrictedMode(true);
  //   }
  // }, []);

  // const isRestrictedEnvironment = () => {
  //   // Check for common school proxy/security headers
  //   const userAgent = navigator.userAgent.toLowerCase();
  //   const isSchoolProxy = userAgent.includes('websense') || 
  //                        userAgent.includes('smoothwall') ||
  //                        userAgent.includes('lightspeed');
  
  //   // Check if WebRTC is supported
  //   const isWebRTCBlocked = !navigator.mediaDevices || 
  //                          !navigator.mediaDevices.getUserMedia;
  
  //   return isSchoolProxy || isWebRTCBlocked || window.location.protocol === 'https:';
  // };

  const toggleFullscreen = () => {
    if (recording && isFullScreen) {
      if (window.confirm('You are currently recording. Do you want to stop recording and exit fullscreen?')) {
        handleStopRecording();
      } else {
        return; // Stay in fullscreen if user cancels
      }
    }
    
    setIsFullScreen(prev => {
      if (prev) {
        dispatch(fullScreenOff());
      } else {
        dispatch(fullScreenOn());
      }
      return !prev;
    });
  };
  
  // Handle route changes and page unload when recording
  useEffect(() => {
    if (!recording) return;

    const handleRouteChange = (e) => {
      // For beforeunload (page refresh/close)
      if (e.type === 'beforeunload') {
        e.preventDefault();
        e.returnValue = 'You have an active recording. Are you sure you want to leave?';
        return e.returnValue;
      }

      // For route changes
      if (window.confirm('You are currently recording. Do you want to stop recording and save?')) {
        handleStopRecording();
      } else {
        // Prevent route change if user cancels
        const currentPath = window.location.pathname;
        window.history.pushState(null, '', currentPath);
        throw new Error('Route change aborted by user');
      }
    };

    // Listen to route changes and page unload
    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('beforeunload', handleRouteChange);
    
    // Handle programmatic navigation (e.g., using react-router)
    const originalPushState = window.history.pushState;
    window.history.pushState = function() {
      originalPushState.apply(window.history, arguments);
      if (recording) {
        handleRouteChange({ type: 'popstate' });
      }
    };
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('beforeunload', handleRouteChange);
      window.history.pushState = originalPushState; // Restore original
    };
  }, [recording]);

  
  // Toggle fullscreen when recording starts/stops
  useEffect(() => {
    if (recording) {
      // Set fullscreen styles
      document.documentElement.style.overflow = 'hidden';
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      
      // Dispatch fullscreen action
      dispatch(fullScreenOn());
      setIsFullScreen(true);
      
      // Force a reflow to ensure styles are applied
      document.body.getBoundingClientRect();
      
      return () => {
        // Cleanup styles when component unmounts or recording stops
        document.documentElement.style.overflow = '';
        document.body.style.margin = '';
        document.body.style.padding = '';
      };
    }
  }, [recording, dispatch]);
  useEffect(() => {
    const handleClickOutside = (e) => {
      const shapeBox = document.querySelector('[data-shape-box]');
      const labelBox = document.querySelector('[data-label-box]');
      const lineBox = document.querySelector('[data-line-box]');
      
      if (showShapeBox && shapeBox && !shapeBox.contains(e.target)) {
        setShowShapeBox(false);
      }
      if (showLabelBox && labelBox && !labelBox.contains(e.target)) {
        setShowLabelBox(false);
      }
      if (showLineBox && lineBox && !lineBox.contains(e.target)) {
        setShowLineBox(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShapeBox, showLabelBox, showLineBox]);

  useEffect(() => {
    const transformer = transformerRef.current;
    if (!transformer) return;
  
    const stage = stageRef.current;
    if (!stage) return;
  
    // Get all selected nodes except the one being edited
    const selectedNodes = selectedIds
      .filter(id => id !== editingTextId) // 
      .map(id => stage.findOne(`#${id}`))
      .filter(node => node); // remove undefined
  
    transformer.nodes(selectedNodes); // 
  }, [selectedIds, editingTextId]);
  

  // useEffect(() => {
  //   if (tool !== 'label') {
  //     setSelectedLabelType('TOOLTIP_DOWN');
  //     setShowLabelBox(false);
  //   }
  // }, [tool]);

  // Timer for recording
  useEffect(() => {
    if (recording && !isPaused) {
      const interval = setInterval(() => setRecordingTime(t => t + 1), 1000);
      setRecordingInterval(interval);
      return () => clearInterval(interval);
    } else if (recordingInterval) {
      clearInterval(recordingInterval);
      setRecordingInterval(null);
    }
    
    if (!recording) {
      setRecordingTime(0);
      setIsPaused(false);
    }
    // eslint-disable-next-line
  }, [recording, isPaused]);

  // Responsive width
  useEffect(() => {
    function handleResize() {
      if (containerRef.current) {
        setStageSize(s => ({
          ...s,
          width: containerRef.current.offsetWidth
        }));
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Dynamically grow height if user draws near the bottom
  const growBoardIfNeeded = useCallback((pointerY) => {
    const threshold = stageSize.height - 300;
    if (pointerY > threshold) {
      setStageSize(s => ({ ...s, height: s.height + window.innerHeight }));
    }
  }, [stageSize.height]);

  // Submit text if clicking outside text input
  useEffect(() => {
    if (!(tool === 'text' && textPos)) return;
    
    function handleClick(e) {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        handleTextSubmit();
      }
    }
    
    document.addEventListener('mousedown', handleClick, true);
    return () => {
      document.removeEventListener('mousedown', handleClick, true);
    };
    // eslint-disable-next-line
  }, [tool, textPos, textInput]);
  
  // Clean up transformer when editing text
  useEffect(() => {
    if (editingTextId) {
      // Clear selection and transformer nodes while editing text
      setSelectedIds([]);
      if (transformerRef.current) {
        try {
          transformerRef.current.nodes([]);
          transformerRef.current.getLayer()?.batchDraw();
        } catch (error) {
          console.warn('Error cleaning up transformer:', error);
        }
      }
    }
  }, [editingTextId]);

  // History management functions
  const pushHistory = (newShapes, force = false) => {
    if (!force && JSON.stringify(shapes) === JSON.stringify(newShapes)) {
      return;
    }
    const currentShapes = JSON.parse(JSON.stringify(shapes));
    if (history.length === 0) {
      setHistory([[]]);
    }
    setHistory(prev => {
      const newHistory = [...prev, currentShapes];
      return newHistory.slice(-100);
    });
    setShapes([...newShapes]);
    setFuture([]);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const previousState = history[history.length - 1];
    setFuture(future => [shapes, ...future]);
    setShapes(previousState);
    setHistory(history.slice(0, -1));
    setSelectedIds([]);
  };

  const handleRedo = () => {
    if (future.length === 0) return;
    setHistory(history => [...history, shapes]);
    const nextState = future[0];
    setShapes(nextState);
    setFuture(future.slice(1));
    setSelectedIds([]);
  };

  // Event handlers
  const handleStageMouseDown = (e) => {
    activePointers.current.add(e.evt.pointerId);
    const stage = e.target.getStage();
    const clickedOnEmpty = e.target === stage;
    if (activePointers.current.size > 1) {
      return;
    }
    if (!clickedOnEmpty) {
        return;
    }
    // Only prevent default if the target is the stage or a drawing element
    if (e.target === e.target.getStage() || e.target.getLayer()) {
      e.evt.preventDefault();
    }
    
    if (showShapeBox) {
      setShowShapeBox(false);
      return;
    }
    if (showLabelBox) {
      setShowLabelBox(false);
    }
    if (showLineBox) {
      setShowLineBox(false);
    }
    
    if (tool === 'eraser') {
      setDrawing(true);
      return;
    }
    
    if (tool === 'select') {
      // Start selection rectangle if clicked on empty space
      if (e.target === e.target.getStage()) {
        const pos = e.target.getStage().getPointerPosition();
        setSelection({ visible: true, x: pos.x, y: pos.y, w: 0, h: 0 });
        setSelectedIds([]);
      }
    } else {
      const stage = e.target.getStage();
      const pointer = stage.getPointerPosition();
      growBoardIfNeeded(pointer.y);

      if (tool === 'pen') {
        // Create a new shape and initialize history if empty
        const newShape = {
            id: uniqueId(),
            type: 'pen',
            points: [pointer.x, pointer.y],
            stroke: strokeColor,
            strokeWidth,
            dash: getDash(lineStyle)
        };
        const newShapes = [...shapes, newShape];
        pushHistory(newShapes);

        // Initialize history with empty state if this is the first shape
        if (history.length === 0) {
          setHistory([[]]);
          }
        setDrawing(true);
      } else if (tool === 'shape') {
        // Create a new shape and initialize history if empty
        const newShape = shapeType === 'arrow' || shapeType === 'line'
          ? {
              id: uniqueId(),
              type: shapeType,
              points: [pointer.x, pointer.y, pointer.x, pointer.y],
              stroke: strokeColor,
              strokeWidth,
              dash: getDash(lineStyle)
            }
          : {
              id: uniqueId(),
              type: shapeType,
              start: { x: pointer.x, y: pointer.y },
              end: { x: pointer.x, y: pointer.y },
              stroke: strokeColor,
              strokeWidth,
              dash: getDash(lineStyle)
            };
        const newShapes = [...shapes, newShape];
        pushHistory(newShapes);

        // Initialize history with empty state if this is the first shape
        if (history.length === 0) {
          setHistory([[]]);
        }
        setDrawing(true);
      } else if (tool === 'text') {
        setTextPos(pointer);
        setTextInput('');
        setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
      } else if (tool === 'label') {
        // Create the new label shape
        const newShape = {
          id: uniqueId(),
          type: 'label',
          labelType: selectedLabelType,
          text: '',
          x: pointer.x,
          y: pointer.y,
          minWidth: 100
        };
        
        // Update shapes state first
        const newShapes = [...shapes, newShape];
        setShapes(newShapes);
        
        // Then update history
        pushHistory(newShapes);
        
        // Hide the label box after placing a label
        setShowLabelBox(false);
      }
    }
  };

  const handleStageMouseMove = (e) => {
    if (activePointers.current.size > 1) return;
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    
    // Skip if it's a touch event and we don't have a pointer position
    if (e.evt.touches && !pointer) return;
    
    // Only prevent default if we're actually drawing
    if (drawing) {
      e.evt.preventDefault();
    }

    if (tool === 'select' && selection.visible) {
      setSelection(sel => ({
        ...sel,
        w: pointer.x - sel.x,
        h: pointer.y - sel.y
      }));
      return;
    }

    if (tool === 'eraser' && drawing) {
      const eraserSize = 1; // Eraser radius
      const eraserBox = {
        x: pointer.x - eraserSize,
        y: pointer.y - eraserSize,
        width: eraserSize * 2,
        height: eraserSize * 2
      };
  
      const nodes = stage.find('Shape');
      const shapesToRemove = nodes
        .filter(node => {
          const box = node.getClientRect();
          return window.Konva.Util.haveIntersection(eraserBox, box);
        })
        .map(node => node.attrs.id);
  
      if (shapesToRemove.length > 0) {
        const updated = shapes.filter(s => !shapesToRemove.includes(s.id));
        pushHistory(updated);
      }
      return;
    }


    if (!drawing) return;

    // Drawing logic
    setShapes((prev) => {
      const updated = [...prev];
      const idx = updated.length - 1;
      const shape = updated[idx];

      if (tool === 'pen') {
        shape.points = shape.points.concat([pointer.x, pointer.y]);
      } else if (tool === 'shape') {
        if (shapeType === 'arrow' || shapeType === 'line') {
          shape.points = [shape.points[0], shape.points[1], pointer.x, pointer.y];
        } else {
          shape.end = { x: pointer.x, y: pointer.y };
        }
      }
      updated[idx] = { ...shape };
      return updated;
    });
  };

  const handleStageMouseUp = (e) => {
    // Only prevent default if we were drawing
    activePointers.current.delete(e.evt.pointerId);
    if (drawing) {
      e.evt.preventDefault();
    }
    
    if (tool === 'select' && selection.visible) {
      const selBox = {
        x: Math.min(selection.x, selection.x + selection.w),
        y: Math.min(selection.y, selection.y + selection.h),
        width: Math.abs(selection.w),
        height: Math.abs(selection.h)
      };
      const stage = stageRef.current.getStage();
      const selected = shapes.filter(s => {
        const node = stage.findOne(`#${s.id}`);
        if (!node) return false;
        const box = node.getClientRect();
        return window.Konva.Util.haveIntersection(selBox, box);
      });
      setSelectedIds(selected.map(s => s.id));
      setSelection({ visible: false, x: 0, y: 0, w: 0, h: 0 });
    }
    setDrawing(false);
  };

  // Keep transformer in sync with selectedIds and track transformations
  useEffect(() => {
    if (tool !== 'select') return;
    if (transformerRef.current && selectedIds.length > 0 && stageRef.current) {
      const stage = stageRef.current.getStage();
      const nodes = selectedIds.map(id => stage.findOne(`#${id}`)).filter(Boolean);
      
      // Configure transformer to maintain stroke width during scaling
      transformerRef.current.nodes(nodes);
      transformerRef.current.getLayer().batchDraw();
      
      // Configure nodes appropriately
      nodes.forEach(node => {
        // Only call strokeScaleEnabled if the node supports it
        if (node.strokeScaleEnabled) {
          node.strokeScaleEnabled(false);
        }
        // For Label nodes, we need to handle their children
        if (node.getType() === 'Label') {
          const textNode = node.findOne('Text');
          if (textNode && textNode.strokeScaleEnabled) {
            textNode.strokeScaleEnabled(false);
          }
        }
      });

      // Add event listener for transformation changes
      let isTransforming = false;

      transformerRef.current.on('transformend', () => {
        if (!isTransforming) return;
        isTransforming = false;
        
        const updatedShapes = shapes.map(shape => {
          if (selectedIds.includes(shape.id)) {
            const node = stage.findOne(`#${shape.id}`);
            if (!node) return shape;
            
            const pos = node.position();
            const scale = node.scale();
            
            // Update shape properties based on its type
            if (shape.type === 'text') {
              return {
                ...shape,
                x: pos.x,
                y: pos.y
              };
            } else if (shape.type === 'pen' || shape.type === 'line' || shape.type === 'arrow') {
              // For lines and arrows, store only start and end points
              const points = shape.points.map((point, i) => {

                if (i % 2 === 0) return point * scale.x + pos.x;

                return point * scale.y + pos.y;

              });
              return {
                ...shape,
                points
              };
            } else if (shape.type === 'rectangle' || shape.type === 'square') {
              const box = node.getClientRect();
              return {
                ...shape,
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height
              };
            } else if (shape.type === 'circle') {
              const box = node.getClientRect();
              return {
                ...shape,
                x: box.x,
                y: box.y,
                radius: box.width / 2
              };
            } else if (shape.type === 'star' || shape.type === 'hexagon' || shape.type === 'triangle') {
              const box = node.getClientRect();
              return {
                ...shape,
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height
              };
            }
          }
          return shape;
        });
        
        // Update shapes and store the new state
        pushHistory(updatedShapes);
      });

      // Clean up event listeners
      return () => {
        if (transformerRef.current) {
          transformerRef.current.off('transformstart');
          transformerRef.current.off('transformend');
        }
      };
    }
  }, [selectedIds, tool, shapes, pushHistory]);

  const handleTextSubmit = (e) => {
    if (e) e.preventDefault();
    if (!textInput.trim() || !textPos) {
      setTextInput('');
      setTextPos(null);
      return;
    }
    
    // Create the new shape
    const newShape = {
      id: uniqueId(),
      type: 'text',
      text: textInput.trim(),
      x: textPos.x,
      y: textPos.y,
      fontSize: 20,
      fill: strokeColor
    };
    
    // Update shapes state first
    const newShapes = [...shapes, newShape];
    setShapes(newShapes);
    
    // Then update history
    pushHistory(newShapes);
    
    // Reset form
    setTextInput('');
    setTextPos(null);
  };

  const handleClear = () => {
    pushHistory([]);
  };

  const handleExport = () => {
    // Save the image data for later use
    const imageData = stageRef.current.toDataURL({ pixelRatio: 2 });
    exportImageRef.current = imageData;
    
    // Reset form state
    setFormMode('export');
    setRecordingTitle('');
    setRecordingDescription('');
    setRecordingSubjectId('');
    setRecordingChapterId('');
    setTitleError('');
    setSubjectError('');
    setChapterError('');
    
    // Fetch subjects and show the form
    dispatch(getSubjectWhiteboard()).then(() => {
      setShowRecordingForm(true);
    });
  };
  
  const handleExportImage = () => {
    try {
      const stage = stageRef.current;
      if (!stage) return;
      
      // Get the full stage dimensions
      const stageWidth = stage.width();
      const stageHeight = stage.height();
      
      // Create a temporary stage to render the full content
      const tempStage = new window.Konva.Stage({
        container: document.createElement('div'),
        width: stageWidth,
        height: stageHeight,
      });
      
      // Create a white background rectangle
      const bgLayer = new window.Konva.Layer();
      const bgRect = new window.Konva.Rect({
        width: stageWidth,
        height: stageHeight,
        fill: 'white',
        x: 0,
        y: 0,
      });
      bgLayer.add(bgRect);
      tempStage.add(bgLayer);
      
      // Clone the current layer to the temporary stage
      const layer = stage.getLayers()[0];
      const tempLayer = layer.clone();
      tempStage.add(tempLayer);
      
      // Get the data URL of the full stage
      const dataUrl = tempStage.toDataURL({
        mimeType: 'image/png',
        quality: 1,
        pixelRatio: 2 // Higher quality
      });

      // Clean up
      tempStage.destroy();
      tempLayer.destroy();
      
      // Upload to server if needed
      if (isFormSubmittedRef.current) {
        const formData = new FormData();
        formData.append('file', dataURLtoFile(dataUrl, (recordingTitle || 'whiteboard') + '.png'));
        dispatch(uploadRecording("image", recordingTitle, recordingDescription, recordingSubjectId, recordingChapterId, formData));
      }
      
      setShowRecordingForm(false);
    } catch (error) {
      console.error('Error exporting image:', error);
      alert('Failed to export image. Please try again.');
    }
  };
  
  // Helper function to convert data URL to file
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  function getDash(styleKey) {
    if (styleKey === 'dashed') return [20, 10];
    if (styleKey === 'dotted') return [2, 10];
    return [];
  }

  const getShapeProps = (shape) => {
    if (!shape.start || !shape.end) return {};
    const { x: x1, y: y1 } = shape.start;
    const { x: x2, y: y2 } = shape.end;
    const minX = Math.min(x1, x2);
    const minY = Math.min(y1, y2);
    const w = Math.abs(x2 - x1);
    const h = Math.abs(y2 - y1);
    return { x1, y1, x2, y2, minX, minY, w, h };
  };

  // ERASER: only attaches onClick when tool === 'eraser'  // ERASER: only attaches onClick when tool === 'eraser'
  const handleEraseShape = (idx) => {
    // Get the current state of all shapes including their transformations
    const currentShapes = shapes.map(shape => ({
      ...shape,
      // For lines/arrows, store transformed points
      points: shape.type === 'pen' || shape.type === 'line' || shape.type === 'arrow'
        ? shape.points.map((point, i) => {
            const node = stageRef.current.findOne(`#${shape.id}`);
            if (!node) return point;
            const pos = node.position();
            const scale = node.scale();
            if (i % 2 === 0) return point * scale.x + pos.x;
            return point * scale.y + pos.y;
          })
        : shape.points
    }));
    
    // Filter out the shape to be erased while preserving transformations
    const updatedShapes = currentShapes.filter((_, i) => i !== idx);
    
    // Store the updated shapes with preserved transformations
    pushHistory(updatedShapes);
  };


  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const image = new window.Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  };

  const handleToolClick = (key) => {
    setTool(key);
    if (key === 'image') {
      // Create file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.style.display = 'none';
      document.body.appendChild(input);
      
      // Handle file selection
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const image = await loadImage(event.target.result);
            const newShape = {
              id: Date.now().toString(),
              type: 'image',
              image: event.target.result,
              x: 0, // Place at top-left corner
              y: 0,
              width: image.width,
              height: image.height,
              rotation: 0
            };
            
            // Add the image to the shapes array
            setShapes(prevShapes => [...prevShapes, newShape]);
            // Update history
            pushHistory([...shapes, newShape]);
          } catch (error) {
            console.error('Error loading image:', error);
          }
        };
        reader.readAsDataURL(file);
      };
      
      // Trigger file selection immediately
      input.click();
      input.remove();
    } else if (key === 'label') {
      setShowLabelBox(true);
    }
  };

  const renderShape = (shape, i) => {
    const isSelected = tool === 'select' && selectedIds.includes(shape.id);
    const selectProps = tool === 'select' ? {
      id: shape.id,
      draggable: isSelected,
      onClick: (e) => {
        e.cancelBubble = true;
        if (e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey) {
          setSelectedIds(ids =>
            ids.includes(shape.id)
              ? ids.filter(id => id !== shape.id)
              : [...ids, shape.id]
          );
        } else {
          setSelectedIds([shape.id]);
        }
      },
      onTap: (e) => {
        e.cancelBubble = true;
        setSelectedIds([shape.id]);
      },
      onDragEnd: (e) => {
        const node = e.target;
        const newPos = node.position();
        
        const updatedShapes = shapes.map(s => {
          if (s.id !== shape.id) return s;
          
          // Create a copy of the shape to avoid direct state mutation
          const updated = { ...s };
          
          // Handle different shape types
          switch (s.type) {
            case 'line':
            case 'arrow':
              // For lines and arrows, calculate movement delta from the first point
              updated.points = s.points
              updated.x = newPos.x;
              updated.y = newPos.y;
              break;
            case 'pen':
              // For pen drawings, just update the position
              updated.x = newPos.x;
              updated.y = newPos.y;
              break;
            case 'rectangle':
            case 'circle':
            case 'square': {
              // For rectangles and squares, use start/end points
              const rectDeltaX = newPos.x - (s.start?.x || 0);
              const rectDeltaY = newPos.y - (s.start?.y || 0);
              
              updated.start = {
                x: newPos.x,
                y: newPos.y
              };
              if (s.end) {
                updated.end = {
                  x: s.end.x + rectDeltaX,
                  y: s.end.y + rectDeltaY
                };
              }
              break;
            }
            case 'star':
            case 'hexagon':
            case 'triangle': {
              // For circle, star, hexagon, and triangle, calculate center-based movement
              const centerX = (s.start?.x + s.end?.x) / 2 || 0;
              const centerY = (s.start?.y + s.end?.y) / 2 || 0;
              const shapeDeltaX = newPos.x - centerX;
              const shapeDeltaY = newPos.y - centerY;
              
              if (s.start && s.end) {
                updated.start = {
                  x: s.start.x + shapeDeltaX,
                  y: s.start.y + shapeDeltaY
                };
                updated.end = {
                  x: s.end.x + shapeDeltaX,
                  y: s.end.y + shapeDeltaY
                };
              } else if (s.x !== undefined && s.y !== undefined) {
                // For shapes using x,y instead of start/end
                updated.x = s.x + shapeDeltaX;
                updated.y = s.y + shapeDeltaY;
              }
              break;
            }
              
            case 'image':
            case 'text':
              updated.x = newPos.x;
              updated.y = newPos.y;
              break;
              
            default:
              updated.x = newPos.x;
              updated.y = newPos.y;
          }
          
          return updated;
        });
        pushHistory(updatedShapes);
      },
      
      onTransformEnd: (e) => {
        const node = e.target;
        if (!node || !node.scaleX || !node.scaleY) return;
        
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const rotation = node.rotation ? node.rotation() : 0;
      
        const updatedShapes = shapes.map(s => {
          if (s.id !== shape.id) return s;
          const updated = { ...s };
      
          // Text
          if (s.type === 'text') {
            const attrs = JSON.parse(JSON.stringify(node.attrs));
            const newFontSize = Math.max(10, (attrs.fontSize || 20) * Math.max(scaleX, scaleY));
            
            updated.fontSize = newFontSize;
            updated.x = node.x();
            updated.y = node.y();
            updated.rotation = rotation;
            
            // Preserve other text properties
            updated.text = attrs.text || '';
            updated.fill = attrs.fill || '#222222';
          }
          // Line / Arrow
          else if (s.type === 'line' || s.type === 'arrow') {
            // Get the current points from the shape
            const points = s.points || [0, 0, 100, 0];
            
            // Calculate the center point of the line
            const centerX = (points[0] + points[2]) / 2;
            const centerY = (points[1] + points[3]) / 2;
            
            // Calculate new points with scaling relative to the center
            const newPoints = [
              (points[0] - centerX) * scaleX + centerX,
              (points[1] - centerY) * scaleY + centerY,
              (points[2] - centerX) * scaleX + centerX,
              (points[3] - centerY) * scaleY + centerY
            ];
            
            // Update the shape with new points, position, and rotation
            updated.points = newPoints;
            updated.x = node.x();
            updated.y = node.y();
            updated.rotation = rotation;
          }
          // Pen (freehand drawing)
          else if (s.type === 'pen') {
            const newPoints = s.points.map((point, i) =>
              i % 2 === 0 ? point * scaleX : point * scaleY
            );
            updated.points = newPoints;
            updated.x = node.x();
            updated.y = node.y();
            updated.rotation = rotation;
          }
      
          // Rectangle / Square / Circle
          else if (s.type === 'rectangle' || s.type === 'square' || s.type === 'circle') {
            const width = Math.abs(s.end.x - s.start.x) * scaleX;
            const height = Math.abs(s.end.y - s.start.y) * scaleY;
      
            updated.start = {
              x: node.x(),
              y: node.y()
            };
            updated.end = {
              x: node.x() + width,
              y: node.y() + height
            };
            updated.rotation = rotation;
          }
      
          // Star shape
          else if (s.type === 'star') {
            const attrs = JSON.parse(JSON.stringify(node.attrs));
            const newInnerRadius = attrs.innerRadius * Math.max(scaleX, scaleY);
            const newOuterRadius = attrs.outerRadius * Math.max(scaleX, scaleY);
            
            updated.innerRadius = newInnerRadius;
            updated.outerRadius = newOuterRadius;
            updated.x = node.x();
            updated.y = node.y();
            updated.rotation = rotation;
            
            // For backward compatibility with start/end points
            const size = newOuterRadius * 2;
            updated.start = {
              x: node.x() - size/2,
              y: node.y() - size/2
            };
            updated.end = {
              x: node.x() + size/2,
              y: node.y() + size/2
            };
          }
          // Hexagon and Triangle (RegularPolygon)
          else if (s.type === 'hexagon' || s.type === 'triangle') {
            const attrs = JSON.parse(JSON.stringify(node.attrs));
            const newRadius = attrs.radius * Math.max(scaleX, scaleY);
            
            updated.radius = newRadius;
            updated.x = node.x();
            updated.y = node.y();
            updated.rotation = rotation;
            
            // For backward compatibility with start/end points
            const size = newRadius * 2;
            updated.start = {
              x: node.x() - size/2,
              y: node.y() - size/2
            };
            updated.end = {
              x: node.x() + size/2,
              y: node.y() + size/2
            };
          }
      
          return updated;
        });
      
        // Reset transform to avoid compounding
        node.scaleX(1);
        node.scaleY(1);
        node.position({ x: 0, y: 0 });
      
        pushHistory(updatedShapes);
      }     
      
    } : {};

    const eraserProps = tool === 'eraser'
      ? { onClick: () => handleEraseShape(i), listening: true }
      : {};

    if (shape.type === 'text') {
      if (editingTextId === shape.id) {
        // Render editable input overlay (absolute, not in canvas)
        return null;
      }
      return (
        <>
          <Text
            key={i}
            id={shape.id}
            text={shape.text}
            x={shape.x}
            y={shape.y}
            fontSize={shape.fontSize}
            fill={shape.fill}
            {...selectProps}
            {...eraserProps}
            draggable={tool === 'select' && selectedIds.includes(shape.id)}
            onDblClick={() => {
              if (tool === 'select' && selectedIds.length === 1 && selectedIds[0] === shape.id) {
                // Save the current shapes state before editing
                const currentShapes = [...shapes];
                setInitialTextState(currentShapes);
                setEditingTextId(shape.id);
                setEditingTextValue(shape.text);
              }
            }}
          />
          {tool === 'select' && selectedIds.includes(shape.id) && (
            <Transformer
              ref={transformerRef}
              rotateEnabled={true}
              anchorSize={8}
              boundBoxFunc={(oldBox, newBox) => newBox}
              nodes={[stageRef.current.findOne(`#${shape.id}`)]}
            />
          )}
        </>
      );
    }
    if (shape.type === 'label') {
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
          <DraggableLabel
            key={shape.id}
            shape={shape}
            isSelected={selectedIds.includes(shape.id)}
            onSelect={() => setSelectedIds([shape.id])}
            onErase={() => {
              const newShapes = shapes.filter(s => s.id !== shape.id);
              setShapes(newShapes);
              pushHistory(newShapes);
            }}
            stageRef={stageRef}
            tool={tool}
            setShapes={setShapes}
            pushHistory={pushHistory}
          />
        </ErrorBoundary>
      );
    }
    if (shape.type === 'pen') {
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <>
          <Line
            key={i}
            id={shape.id}
            points={shape.points}
            stroke={shape.stroke}
            strokeWidth={shape.strokeWidth}
            lineCap="round"
            lineJoin="round"
            dash={shape.dash}
            tension={0.5}
            globalCompositeOperation="source-over"
            {...selectProps}
            {...eraserProps}
            draggable={tool === 'select' && selectedIds.includes(shape.id)}
          />
          {tool === 'select' && selectedIds.includes(shape.id) && (
            <Transformer
              ref={transformerRef}
              rotateEnabled={true}
              anchorSize={8}
              boundBoxFunc={(oldBox, newBox) => newBox}
              nodes={[stageRef.current.findOne(`#${shape.id}`)]}
            />
          )}
        </>
        </ErrorBoundary>
      );
    }
    if (shape.type === 'arrow') {
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <>
          <Arrow
            key={i}
            id={shape.id}
            points={shape.points}
            stroke={shape.stroke}
            strokeWidth={shape.strokeWidth}
            pointerLength={15}
            pointerWidth={15}
            dash={shape.dash}
            {...selectProps}
            {...eraserProps}
            draggable={tool === 'select' && selectedIds.includes(shape.id)}
          />
          {tool === 'select' && selectedIds.includes(shape.id) && (
            <Transformer
              ref={transformerRef}
              rotateEnabled={true}
              anchorSize={8}
              boundBoxFunc={(oldBox, newBox) => newBox}
              nodes={[stageRef.current.findOne(`#${shape.id}`)]}
            />
          )}
        </>
        </ErrorBoundary>
      );
    }
    if (shape.type === 'line') {
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <>
          <Line
            key={i}
            id={shape.id}
            points={shape.points}
            stroke={shape.stroke}
            strokeWidth={shape.strokeWidth}
            lineCap="round"
            dash={shape.dash}
            {...selectProps}
            {...eraserProps}
            draggable={tool === 'select' && selectedIds.includes(shape.id)}
          />
          {tool === 'select' && selectedIds.includes(shape.id) && (
            <Transformer
              ref={transformerRef}
              rotateEnabled={true}
              anchorSize={8}
              boundBoxFunc={(oldBox, newBox) => newBox}
              nodes={[stageRef.current.findOne(`#${shape.id}`)]}
            />
          )}
        </>
        </ErrorBoundary>
      );
    }
    if (shape.type === 'rectangle') {
      const { minX, minY, w, h } = getShapeProps(shape);
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <>
          <Rect
            key={i}
            id={shape.id}
            x={minX}
            y={minY}
            width={w}
            height={h}
            stroke={shape.stroke}
            strokeWidth={shape.strokeWidth}
            dash={shape.dash}
            {...selectProps}
            {...eraserProps}
            draggable={tool === 'select' && selectedIds.includes(shape.id)}
          />
          {tool === 'select' && selectedIds.includes(shape.id) && (
            <Transformer
              ref={transformerRef}
              rotateEnabled={true}
              anchorSize={8}
              boundBoxFunc={(oldBox, newBox) => newBox}
              nodes={[stageRef.current.findOne(`#${shape.id}`)]}
            />
          )}
        </>
        </ErrorBoundary>
      );
    }
    if (shape.type === 'square') {
      const { x1, y1, x2, y2 } = getShapeProps(shape);
      const size = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
      const sx = x2 > x1 ? x1 : x1 - size;
      const sy = y2 > y1 ? y1 : y1 - size;
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <Rect
          key={i}
          id={shape.id}
          x={sx}
          y={sy}
          width={size}
          height={size}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          dash={shape.dash}
          {...selectProps}
          {...eraserProps}
        />
        </ErrorBoundary>
      );
    }

    if (shape.type === 'image') {
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <DraggableComponent
          key={shape.id}
          shape={shape}
          isSelected={selectedIds.includes(shape.id)}
          onSelect={() => setSelectedIds([shape.id])}
          onErase={() => {
            setShapes(prevShapes => {
              const newShapes = prevShapes.filter(s => s.id !== shape.id);
              pushHistory(newShapes);
              return newShapes;
            });
          }}
          stageRef={stageRef}
          tool={tool}
          setShapes={setShapes}
          pushHistory={pushHistory}
        />
        </ErrorBoundary>
      );
    }
          
    if (shape.type === 'circle') {
      const { x1, y1, x2, y2 } = getShapeProps(shape);
      const radius = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <Circle draggable
          key={i}
          id={shape.id}
          x={x1}
          y={y1}
          radius={radius}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          dash={shape.dash}
          {...selectProps}
          {...eraserProps}
        />
        </ErrorBoundary>
      );
    }
    if (shape.type === 'triangle') {
      const { x1, y1, x2, y2 } = getShapeProps(shape);
      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;
      const size = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <RegularPolygon
          key={i}
          id={shape.id}
          x={cx}
          y={cy}
          sides={3}
          radius={size / 2}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          dash={shape.dash}
          {...selectProps}
          {...eraserProps}
        />
        </ErrorBoundary>
      );
    }
    if (shape.type === 'hexagon') {
      const { x1, y1, x2, y2 } = getShapeProps(shape);
      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;
      const size = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <RegularPolygon
          key={i}
          id={shape.id}
          x={cx}
          y={cy}
          sides={6}
          radius={size / 2}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          dash={shape.dash}
          {...selectProps}
          {...eraserProps}
        />
        </ErrorBoundary>
      );
    }
    if (shape.type === 'star') {
      const { x1, y1, x2, y2 } = getShapeProps(shape);
      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;
      const size = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
      return (
        <ErrorBoundary key={`label-${shape.id}`}>
        <Star
          key={i}
          id={shape.id}
          x={cx}
          y={cy}
          numPoints={5}
          innerRadius={size / 4}
          outerRadius={size / 2}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          dash={shape.dash}
          {...selectProps}
          {...eraserProps}
        />
        </ErrorBoundary>
      );
    }
    return null;
  };

  const handleBeforeUnload = (e) => {
    if (recording) {
      e.preventDefault();
      e.returnValue = 'You have an active recording. Are you sure you want to leave?';
      return e.returnValue;
    }
  };

  const handleStartRecording = async () => {    
    try {
      // Set form mode to recording
      setFormMode('recording');
      
      // This will trigger the fullscreen effect through the useEffect above
      const stage = stageRef.current;
      const scrollDiv = scrollDivRef.current;
  
      if (!stage || !scrollDiv) {
        alert('Whiteboard or scroll container not found');
        return;
      }
      
      // Get the full viewport dimensions
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Update the stage size to match the full viewport
      stage.width(viewportWidth);
      stage.height(viewportHeight);
      stage.draw();
  
      // Create an offscreen canvas to draw the visible content
      const offscreen = document.createElement('canvas');
      const offctx = offscreen.getContext('2d');
      offscreen.width = viewportWidth;
      offscreen.height = viewportHeight;
  
      // Continuously update offscreen canvas with visible portion
      const drawToOffscreen = () => {
        const scrollTop = scrollDiv.scrollTop;
  
        // Clear canvas
        offctx.fillStyle = '#fff';
        offctx.fillRect(0, 0, viewportWidth, viewportHeight);
  
        // Get the visible portion of the stage as a canvas
        const visibleCanvas = stage.toCanvas({
          x: 0,
          y: scrollTop,
          width: viewportWidth,
          height: viewportHeight,
          pixelRatio: 1
        });
  
        // Draw it into the offscreen canvas
        offctx.drawImage(visibleCanvas, 0, 0);
      };
  
      // Draw 30 times per second
      const drawInterval = setInterval(drawToOffscreen, 1000 / 30);
      setRecordingInterval(drawInterval);
  
      const canvasStream = offscreen.captureStream(30);
      let audioStream = null;
      
      try {
        audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        console.warn('Could not access microphone, continuing without audio:', error);
      }
      
      const combinedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...(audioStream ? audioStream.getAudioTracks() : [])
      ]);
      
      const chunks = [];
      const options = { 
        mimeType: 'video/webm;codecs=vp9',
        audio: !!audioStream
      };
      const recorder = new MediaRecorder(combinedStream, options);
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = () => {
        // Clean up streams
        canvasStream.getTracks().forEach(track => track.stop());
        if (audioStream) {
          audioStream.getTracks().forEach(track => track.stop());
        }
        
        // Clear the interval when recording stops
        if (recordingInterval) {
          clearInterval(recordingInterval);
          setRecordingInterval(null);
        }

        if (chunks.length > 0) {
          // Save the recording data for later use when form is submitted
          recordingBlobRef.current = new Blob(chunks, { type: 'video/webm' });
        }
      };
  
      // Start timer and recording
      startTimer();
      recorder.start(1000); // Request data every second for better pause/resume handling
      setMediaRecorder(recorder);
      setRecording(true);
      
      // Add beforeunload listener for route changes
      window.addEventListener('beforeunload', handleBeforeUnload);
    } catch (err) {
      console.error('Error starting recording:', err);
      alert('Could not start recording: ' + (err.message || 'Unknown error'));
      // Reset states on error
      setRecording(false);
      setRecordingTime(0);
      setIsPaused(false);
    }
  };

  const togglePauseRecording = () => {
    if (!mediaRecorder) return;
    
    try {
      if (isPaused) {
        // Only resume if currently paused and recorder is in paused state
        if (mediaRecorder.state === 'paused') {
          mediaRecorder.resume();
          startTimer();
          setIsPaused(false);
        }
      } else {
        // Only pause if currently recording
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.pause();
          stopTimer();
          setIsPaused(true);
        }
      }
    } catch (error) {
      console.error('Error toggling recording pause state:', error);
    }
  };
  

  // Handle stopping the recording (pauses and shows the form)
  const handleStopRecording = async () => {
    try {
      // Stop the timer first
      stopTimer();
      
      // Then handle media recorder
      if (mediaRecorder) {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.pause();
        }
        if (mediaRecorder.stream) {
          mediaRecorder.stream.getTracks().forEach(track => track.stop());
        }
      }
      
      setRecording(false);
      setMediaRecorder(null);
      
      // Remove beforeunload listener
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Exit fullscreen when stopping recording
      dispatch(fullScreenOff());
      setIsFullScreen(false);
      setShowRecordingForm(true);
      dispatch(getSubjectWhiteboard());
    } catch (error) {
      console.error('Error stopping recording:', error);
      // Reset states on error
      handleCancel();
    }
  }


  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    } 

    setShowRecordingForm(false);
    isFormSubmittedRef.current = true;
    
    try {
      if (formMode === 'recording') {
        // Handle recording submission
        if (recordingBlobRef.current) {
          setUploadMessage('Uploading video...');
          const formData = new FormData();
          formData.append('file', recordingBlobRef.current, `whiteboard_${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.webm`);
          await dispatch(uploadRecording("video", recordingTitle, recordingDescription, recordingSubjectId, recordingChapterId, formData));
          recordingBlobRef.current = null;
          setUploadMessage('');
        }
        if (recordingInterval) {
          clearInterval(recordingInterval);
          setRecordingInterval(null);
        }
        
        setRecording(false);
        setMediaRecorder(null);
      } else if (formMode === 'export') {
        // Handle image export
        setUploadMessage('Exporting image...');
        try {
          await handleExportImage();
          setUploadMessage('');
        } catch (error) {
          setUploadMessage('');
          throw error;
        }
      }
      
      // Reset form fields
      setRecordingTitle('');
      setRecordingDescription('');
      setRecordingSubjectId('');
      setRecordingChapterId('');
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred during upload. Please try again.');
    } finally {
      setUploadMessage('');
    }
  };
  
  // Handle canceling the recording entirely
  const handleCancelRecording = () => {
    if (!window.confirm('Are you sure you want to cancel this recording? This cannot be undone.')) {
      return;
    }
    
    // Prevent form from showing
    isFormSubmittedRef.current = false;
    setShowRecordingForm(false);
    
    // Stop the media recorder if it exists
    if (mediaRecorder) {
      try {
        // Temporarily remove the onstop handler to prevent form from showing
        const originalOnStop = mediaRecorder.onstop;
        mediaRecorder.onstop = null;
        
        if (mediaRecorder.state === 'recording' || mediaRecorder.state === 'paused') {
          mediaRecorder.stop();
        }
        
        // Stop all tracks in the stream
        const stream = mediaRecorder.stream || mediaRecorder._stream;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        
        // Restore the original handler
        mediaRecorder.onstop = originalOnStop;
      } catch (error) {
        console.error('Error stopping media recorder:', error);
      } finally {
        setMediaRecorder(null);
      }
    }
    
    // Clear the interval and reset states
    stopTimer();
    setRecording(false);
    setRecordingTime(0);
    setShowRecordingForm(false);
    
    // Reset form
    setRecordingTitle('');
    setRecordingDescription('');
    setRecordingSubjectId('');
    setRecordingChapterId('');
  }

  const validateForm = () => {
    if (!recordingTitle.trim()) {
      setTitleError('Title is required');
    }
    else if (!recordingSubjectId) {
      setSubjectError('Please select a subject');
    }
    else if (!recordingChapterId) {
      setChapterError('Please select a chapter');
    } else {
      return true;
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      setShowRecordingForm(false);
      isFormSubmittedRef.current = false;
      // Reset form
      setRecordingTitle('');
      setRecordingDescription('');
      setRecordingSubjectId('');
      setRecordingChapterId('');
      setTitleError('');
      setSubjectError('');
      setChapterError('');
    }
  }

  const handleChapterChange = (subjectId) => {
    setTitleError('');
    setSubjectError('');
    setChapterError('');
    setRecordingSubjectId(subjectId);
    setRecordingChapterId(''); // Reset chapter when subject changes
    if (subjectId) {
      dispatch(getChapters(subjectId));
    }
  };

  const renderLabelBox = () => (
    <div
      data-label-box
      className='whiteboard-box'
    >
      <div className='whiteboard-box-content'>
        {LABEL_PICKER.map(opt => (
          <button
            key={opt.key}
            onClick={() => {
              setSelectedLabelType(opt.key);
              setShowLabelBox(false);
            }}
            title={opt.label}
            style={{
              background: selectedLabelType === opt.key ? '#e0e0e0' : '#fff',
            }}
          >
            {opt.icon}
          </button>
        ))}
      </div>
    </div>
  );

  const renderShapeBox = () => (
    <div 
      data-shape-box
      className='whiteboard-box'
    >
      <div className='whiteboard-box-content'>
        {SHAPES.map(shape => (
          <button
            key={shape.key}
            onClick={() => {
              setShapeType(shape.key);
              setShowShapeBox(false);
            }}
            title={shape.label}
            style={{
              background: shapeType === shape.key ? '#e0e0e0' : '#fff',
            }}
          >
            {shape.icon}
          </button>
        ))}
      </div>
    </div>
  );

  const renderLineBox = () => (
    <div 
      data-shape-box
      className='whiteboard-box'
    >
      <div className='whiteboard-box-content'>
        {LINE_TYPES.map(style => (
          <button
            key={style.key}
            onClick={() => {
              setTool('shape');
              setLineStyle(style.key);
              setShowLineBox(false);  
            }}
            title={style.label}
            style={{
              background: lineStyle === style.key ? '#e0e0e0' : '#fff',
            }}
          >
            {style.icon}
          </button>
        ))}
      </div>
    </div>
  );

  const formatTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const renderRestrictedWarning = () => (
    <div className="restricted-warning">
      <p>Some features are limited in this environment. For best experience, use a modern browser with WebRTC support.</p>
    </div>
  );

  return (
    <Fragment>
      {<div className="mobile-coming">
        <div className="mobile-inner-hide">
            <img src="/images/mobile-error.svg" alt='' />
            <h4>Please visit the website on your Desktop/Laptop</h4>
            <p>Mobile view coming soon</p>
        </div>

    </div>}
    <div className='whiteboard-module'>
      {isUploading && <Loader open={isUploading} message={uploadMessage} />}
      <ErrorBoundary>
          <div
            ref={containerRef}
            className='whiteboard-container'
            style={{
              width: isFullScreen ? window.innerWidth*0.99 : '78vw',
              minHeight: window.innerHeight*0.5
            }}
          >
            {/* TOOLBAR */}
            <div className='whiteboard-toolbar'>
              {TOOL_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => {
                    handleToolClick(opt.key);
                    setSelectedIds([]);
                    if (opt.key === 'shape') setShowShapeBox(v => !v);
                    else setShowShapeBox(false);
                    if (opt.key === 'line') setShowLineBox(v => !v);
                    else setShowLineBox(false);
                  }}
                  title={opt.label}
                  className='whiteboard-toolbar-button'
                  style={{
                    background: tool === opt.key ? '#e0e0e0' : '#fff',
                  }}
                >
                  {opt.icon}
                </button>
              ))}
              <button onClick={handleUndo} disabled={history.length === 0} title="Undo"
                className='whiteboard-toolbar-button'>
                <FaUndo />
              </button>
              <button onClick={handleRedo} disabled={future.length === 0} title="Redo"
                className='whiteboard-toolbar-button'>
                <FaRedo />
              </button>
              <button onClick={handleClear} title="Clear"
                className='whiteboard-toolbar-button'>
                <FaTrash />
              </button>
              <button onClick={handleExport} title="Export"
                className='whiteboard-toolbar-button'>
                <FaSave />
              </button>
              <button 
                onClick={toggleFullscreen}
                title={isFullScreen ? 'Exit Full Screen' : 'Enter Full Screen'}
                className='whiteboard-toolbar-button'
                style={{
                  background: isFullScreen ? '#f0f0f0' : '#fff',
                  transition: 'background 0.2s ease'
                }}
              >
                {isFullScreen ? <FaCompress /> : <FaExpand />}
              </button>
              <input
                type="color"
                value={strokeColor}
                onChange={e => setStrokeColor(e.target.value)}
                className='whiteboard-color-button'
                disabled={tool === 'eraser'}
                title="Stroke Color"
              />
              <input
                type="range"
                min={1}
                max={20}
                value={strokeWidth}
                onChange={e => setStrokeWidth(Number(e.target.value))}
                className='whiteboard-range-button'
                disabled={tool === 'eraser'}
                title="Stroke Width"
              />
              {/* Recording Button */}
              <div className='whiteboard-recording'>
                <button
                  className='whiteboard-recording-button'
                  onClick={recording ? handleStopRecording : handleStartRecording}
                  title={recording ? "Stop Recording" : "Start Recording"}
                  style={{
                    background: recording ? '#e53935' : '#fff',
                    color: recording ? '#fff' : '#e53935'
                  }}
                >
                  {recording ? <FaStop /> : <MdFiberManualRecord />}
                </button>
                {recording && (
                  <>
                    <button
                      onClick={togglePauseRecording}
                      title={isPaused ? "Resume Recording" : "Pause Recording"}
                      className='whiteboard-recording-pause'
                      style={{
                        background: isPaused ? '#4caf50' : '#ff9800',
                        color: '#fff',
                        margin: '0 5px'
                      }}
                    >
                      {isPaused ? <FaPlay /> : <FaPause />}
                      {isPaused ? ' Resume' : ' Pause'}
                    </button>
                    <button
                      onClick={handleCancelRecording}
                      title="Cancel Recording"
                      className='whiteboard-recording-cancel'
                    >
                      <FaTimes /> Cancel
                    </button>
                    <span style={{ 
                      color: isPaused ? '#ff9800' : '#e53935',
                      fontWeight: 'bold', 
                      fontSize: 17,
                      minWidth: '60px',
                      textAlign: 'center',
                      opacity: isPaused ? 0.7 : 1
                    }}>
                      {formatTime(recordingTime)}
                      {isPaused && ' (Paused)'}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* SHAPE PICKER POPOVER */}
            {tool === 'shape' && showShapeBox && (
              <ErrorBoundary key="shape-box">
                {renderShapeBox()}
              </ErrorBoundary>
            )}
            {/* LABEL PICKER POPOVER */}
            {tool === 'label' && showLabelBox && (
              <ErrorBoundary key="label-box">
                {renderLabelBox()}
              </ErrorBoundary>
            )}
            {/* LINE PICKER POPOVER */}
            {tool === 'line' && showLineBox && (
              <ErrorBoundary key="line-box">
                {renderLineBox()}
              </ErrorBoundary>
            )}
            
            {/* Recording Form Modal */}
            {showRecordingForm && (
          <div className='whiteboard-recording-form'>
            <div className='whiteboard-recording-form-content'>
              <h2 className="text-xl font-bold mb-4">
              {formMode === 'recording' ? 'Save Recording' : 'Export Image'}
            </h2>
              <form onSubmit={handleFormSubmit}>
                <div className="mb-4">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={recordingTitle}
                    onChange={(e) => {setRecordingTitle(e.target.value); setTitleError('')}}
                    className="form-field"
                    style={{
                      border: titleError ? '1px solid red' : '1px solid #ddd',
                    }}
                  />
                  {titleError && <div style={{ color: 'red', fontSize: '12px' }}>{titleError}</div>}
                </div>
                
                <div className="mb-4">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={recordingDescription}
                    onChange={(e) => {setRecordingDescription(e.target.value); }}
                    className='form-field'
                  />
                </div>
                
                <div className="mb-4">
                  <label>Subject *</label>
                  <select
                    name="subjectId"
                    value={recordingSubjectId}
                    onChange={(e) => {handleChapterChange(e.target.value); setTitleError('')}}
                    className="form-field"
                    style={{
                      border: subjectError ? '1px solid red' : '1px solid #ddd',
                    }}
                  >
                    <option value="">Select Subject</option>
                    {subjectWhiteboard && subjectWhiteboard.map(subject => (
                      <option key={subject._id} value={subject._id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                  {subjectError && <div style={{ color: 'red', fontSize: '12px' }}>{subjectError}</div>}
                </div>
                
                <div className="mb-4">
                  <label>Chapter *</label>
                  <select
                    name="chapterId"
                    value={recordingChapterId}
                    onChange={(e) => {setRecordingChapterId(e.target.value); setTitleError('')}}
                    disabled={!recordingSubjectId}
                    className="form-field"
                    style={{
                      border: chapterError ? '1px solid red' : '1px solid #ddd',
                      opacity: recordingSubjectId ? 1 : 0.7
                    }}
                  >
                    <option value="">Select Chapter</option>
                    {chapterList && chapterList.map(chapter => (
                      <option key={chapter._id} value={chapter._id}>
                        {chapter.name}
                      </option>
                    ))}
                  </select>
                  {chapterError && <div style={{ color: 'red', fontSize: '12px' }}>{chapterError}</div>}
                </div>
                
                <div className="form-button">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="form-cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="form-submit-button"
                  >
                    {formMode === 'export' ? 'Save Image' : 'Save Recording'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        <div
              ref={scrollDivRef}
              className='whiteboard-scroll-div'
              style={{
                maxHeight: isFullScreen ? window.innerHeight-120 : window.innerHeight - 200,
              }}
            >
              {/* Text input overlay for adding text */}
              {tool === 'text' && textPos && (
                <ErrorBoundary key="text-box">
                  <form
                    onSubmit={handleTextSubmit}
                    className='whiteboard-text-tool'
                    style={{
                      left: textPos.x,
                      top: textPos.y,
                    }}
                  >
                    <input
                      ref={inputRef}
                      autoFocus
                      value={textInput}
                      onChange={e => setTextInput(e.target.value)}
                      className='whiteboard-text-input'
                    />
                  </form>
                </ErrorBoundary>
              )}

              {/* Text input overlay for editing text */}
              {editingTextId && (() => {
                const shape = shapes.find(s => s.id === editingTextId);
                if (!shape) return null;
                
                // Get the stage position to offset the input correctly
                const stage = stageRef.current;
                const stageBox = stage?.container().getBoundingClientRect();
                
                const handleSave = () => {
                  if (!editingTextId) return;
                  
                  const shape = shapes.find(s => s.id === editingTextId);
                  if (!shape) {
                    setEditingTextId(null);
                    setInitialTextState(null);
                    return;
                  }
                  
                  // Only update if text actually changed
                  if (shape.text !== editingTextValue) {
                    // Create new shapes array with updated text
                    const newShapes = shapes.map(s =>
                      s.id === editingTextId ? { ...s, text: editingTextValue } : s
                    );
                    
                    // Push the initial state to history if available
                    if (initialTextState) {
                      pushHistory(initialTextState);
                    }
                    
                    // Update the shapes with new text
                    setShapes(newShapes);
                    
                    // Push the new state to history
                    pushHistory(newShapes);
                  }
                  
                  // Reset editing state
                  setEditingTextId(null);
                  setInitialTextState(null);
                };
                
                const handleKeyDown = (e) => {
                  if (e.key === 'Enter') {
                    handleSave();
                    e.preventDefault();
                  } else if (e.key === 'Escape') {
                    setEditingTextId(null);
                    e.preventDefault();
                  }
                };
                
                return (
                  <div
                    className='whiteboard-text-edit'
                    onMouseDown={(e) => {
                      // Only handle clicks directly on the overlay
                      if (e.target === e.currentTarget) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSave();
                      }
                    }}
                  >
                    <div
                      className='whiteboard-text-edit-div'
                      style={{
                        left: `${(stageBox?.left || 0) + shape.x}px`,
                        top: `${(stageBox?.top || 0) + shape.y}px`,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        ref={inputRef}
                        className='whiteboard-text-edit-input'
                        style={{
                          fontSize: shape.fontSize || 20,
                          fontFamily: shape.fontFamily || 'Arial',
                        }}
                        value={editingTextValue}
                        autoFocus
                        onChange={e => setEditingTextValue(e.target.value)}
                        onBlur={handleSave}
                        onKeyDown={handleKeyDown}
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                  </div>
                );
              })()}
              <div className="whiteboard-canvas-container">
                <Stage
                  width={isFullScreen ? window.innerWidth * 0.9 : stageSize.width*0.9}
                  height={stageSize.height}
                  ref={stageRef}
                  style={{ 
                    background: '#fff', 
                    borderRadius: 8, 
                    display: 'block',
                    touchAction: 'manipulation'
                  }}
                  onPointerDown={handleStageMouseDown}
                  onPointerMove={handleStageMouseMove}
                  onPointerUp={handleStageMouseUp}
                  onPointerCancel={handleStageMouseUp}
                  onMouseDown={handleStageMouseDown}
                  onMouseMove={handleStageMouseMove}
                  onMouseUp={handleStageMouseUp}
                  onMouseCancel={handleStageMouseUp}
                >
                <Layer>
                  <Rect x={0} y={0} width={stageSize.width} height={stageSize.height} fill="#fff" listening={false} />
                  {shapes.map((shape, i) => (
                    <React.Fragment key={shape.id || `shape-${i}`}>
                      {renderShape(shape, i)}
                    </React.Fragment>
                  ))}
                  {/* Selection rectangle */}
                  {tool === 'select' && selection.visible && (
                    <Rect
                      ref={selectionRectRef}
                      x={selection.x}
                      y={selection.y}
                      width={selection.w}
                      height={selection.h}
                      fill="rgba(0,161,255,0.2)"
                      stroke="#00a1ff"
                      dash={[4, 4]}
                      listening={false}
                    />
                  )}
                  {/* Transformer for selection */}
                  {tool === 'select' && selectedIds.length > 0 && (
                    <Transformer
                      ref={transformerRef}
                      rotateEnabled={true}
                      anchorSize={8}
                      boundBoxFunc={(oldBox, newBox) => newBox}
                    />
                  )}
                </Layer>
                </Stage>
              </div>
            </div>
          </div>
      </ErrorBoundary>
    </div>
    </Fragment>
  );
}

export default Whiteboard;
